import ApiProxy from "../../ts/ApiProxy";
import GS from "../../ts/GlobalSetting";
import Tools from "../../ts/Tools";
import Utility from "../../ts/sframe/Utility";
import ViewportRoot, { getViewportName } from "../../ts/sframe/ViewportRoot";
import Popups from "../popups/Popups";
import SearchPopup from "../popups/SearchPopup";
import ImgHeader from "../shared/ImgHeader";
import CollapseWrapper from "../ui/CollapseWrapper";
import SearchFilter from "./SearchFilter";
import SearchResult from "./SearchResult";
import SearchSearchbar from "./SearchSearchbar";
import { Search } from "./SearchTypes";

type QUERY = { input: Search.SearchInput, query: string, queryUrl: string };
const DEFAULT_INPUT: Search.SearchInput = {
  "order": "熱門活動"
};
let _priceSetting: Search.Price,
  _siteList: string[];

export default class SearchManager {
  private static _searchPopup: SearchPopup;
  private static _searchBar: SearchSearchbar;
  private static _searchFilter: SearchFilter;
  static _searchResult: SearchResult;
  private static _lastQuery: QUERY = undefined;
  private static _queueQuery: QUERY = undefined;
  private static _lastHeaderData: Search.HeaderData;
  static _input: Search.SearchInput;
  static _inputQueryObj: any;

  static init() {
    let pageData: Search.PageData = ApiProxy.getPageData();
    this._input = Object.assign({}, DEFAULT_INPUT);
    _priceSetting = pageData.filter_setting.price;
    _siteList = pageData.filter_setting.site_option;
    this._searchResult = Tools.seekComponentByName("search-result");
    this._searchResult.emitter.on("apiLoaded", () => {
      console.log("apiLoaded");
      if (this._queueQuery) {
        this._lastQuery = this._queueQuery;
        this._queueQuery = undefined;
        this._searchResult.insertRequest(this._lastQuery.input);
      } else {
        this._searchResult.updateResults(this._lastQuery.queryUrl);
        this.intoIdle();
      }
    });

    this._searchResult.emitter.on("pageIndexChange", (pageIndex: number) => {
      this._input.page_index = pageIndex;
      this.generateInputQueryObj();
      this.requestSearch();
    });

    this._searchResult.emitter.on("resultUpdated", (totalCount: number, headerData: Search.HeaderData) => {
      this._searchBar.setTotalCount(totalCount);
      if (!this._lastQuery)
        return;
      this.updateHeader(headerData);
    });

    this._searchResult.autoLoadMark.emitter.on("trigger", () => {
      if (this._searchResult.getState() !== "idle")
        throw new Error("unexped error: autoLoadMark 應該在無法在 idle 狀態下被觸發");
      this._searchResult.toggleAutoLoad(false);
      let newPageIndex = this._searchResult.getNextPageIndex();
      if (newPageIndex !== undefined) {
        this._input.page_index = newPageIndex;
        this.generateInputQueryObj();
        this.requestSearch("add");
      }
    });

    ViewportRoot.emitter.on("majorChanged", () => {
      if (getViewportName() !== "mobile") {
        this._searchResult.toggleAutoLoad(false);
      } else if (this._searchResult.getState() === "idle") {
        this._searchResult.toggleAutoLoad(true);
      }
    });

    let searchPopup: SearchPopup = this._searchPopup = Popups.getInstance("search-popup") as SearchPopup;
    searchPopup.activeExtraFilter(_priceSetting, _siteList);
    searchPopup.emitter.on("requestSearch", () => {
      this._input.page_index = 0;
      this.applyInput(searchPopup.input);
      this.requestSearch();
    });

    let searchBar: SearchSearchbar = this._searchBar = Tools.seekComponentByName("search-bar");
    searchBar.emitter.on("change", (type) => {
      this._input.page_index = 0;
      this.applyInput(searchBar.input);
      this.requestSearch();
    });

    let searchFilter: SearchFilter = this._searchFilter = Tools.seekComponentByName("search-filter");
    searchFilter.emitter.on("change", (type) => {
      this._input.page_index = 0;
      this.applyInput(searchFilter.input);
      this.requestSearch();
    });
  }

  static intoIdle() {
    console.log("已無新的搜尋請求, 將進入 idle 狀態");
    if (getViewportName() === "mobile") {
      this._searchResult.toggleAutoLoad(true);
    }
  }

  static syncInputs() {
    this._searchFilter.syncInput(this._input);
    this._searchBar.syncInput(this._input);
    this._searchPopup.syncInput(this._input);
  }

  static reset(input?: Search.SearchInput, requestNow: boolean = true) {
    this._input = input ? Object.assign({}, input) : Object.assign({}, DEFAULT_INPUT);
    this.generateInputQueryObj();
    this._searchBar.setMobileInputText(this.getSearchText());
    this.syncInputs();
    if (requestNow)
      this.requestSearch();
  }

  /**
   * 套用網址參數上的搜尋設定
   */
  static applyUrlQuery() {
    let queryInput: Search.SearchInput = Object.assign({}, DEFAULT_INPUT);
    let p = Utility.urlParams;
    this._searchBar.testOrderInput(p["order"], queryInput);
    this._searchBar.testKeywordInput(p["keyword"], queryInput);
    this._searchBar.testSortInput(p["sort"], queryInput);
    this._searchBar.testDateInput(p["start-date"], p["end-date"], queryInput);
    this._searchFilter.testTimeInput(p["start-time"], p["end-time"], queryInput);
    this._searchFilter.testPriceInput(p["min-price"], p["max-price"], queryInput);
    this._searchFilter.testSiteInput(p["site-list"], queryInput);
    this.reset(queryInput, false);
    let queryString = generateSearchQuery(this._inputQueryObj),
      queryUrl = GS.search.getQueryUrl(queryString);
    this._searchResult.getPager().setQueryUrl(queryUrl);
  }

  /**
   * 更新頁面抬頭
   * @param headerData 抬頭資料
   * @returns 是否有更新
   */
  private static updateHeader(headerData: Search.HeaderData) {
    if (!headerData)
      return false;
    if (this._lastHeaderData) {
      let lastString = JSON.stringify(this._lastHeaderData),
        nowString = JSON.stringify(headerData);
      if (lastString === nowString)
        return false;
    }
    this._lastHeaderData = headerData;
    let featureCollapse: CollapseWrapper = Tools.seekComponentByClass("feature-collapse"),
      imgHeader: ImgHeader = Tools.seekComponentByName("img-header");
    if (headerData.text_1 || headerData.text_2) {
      let $text1 = featureCollapse.$root.find(".text-1"),
        $text2 = featureCollapse.$root.find(".text-2");

      if (headerData.text_1)
        $text1.text(headerData.text_1).attr("display", "block");
      else
        $text1.attr("display", "none");

      if (headerData.text_2)
        $text2.text(headerData.text_2).attr("display", "block");
      else
        $text2.attr("display", "none");

      featureCollapse.toggle(false);
    } else {
      featureCollapse.toggle(true);
    }

    imgHeader.change(this._lastHeaderData);

    if (getViewportName() !== "mobile")
      Tools.scrollToElement("on-search-mark");

    return true;
  }

  private static applyInput(input: Search.SearchInput) {
    this._input = Object.assign({}, this._input, input);

    if (this._input.min_price === _priceSetting.min && this._input.max_price === _priceSetting.max) {
      delete this._input.min_price;
      delete this._input.max_price;
    }

    this.generateInputQueryObj();
    this._searchBar.setMobileInputText(this.getSearchText());

    this.syncInputs();
  }

  private static generateInputQueryObj() {
    this._inputQueryObj = {};
    let key: string;

    for (key in this._input) {
      ///@ts-ignore
      let value = this._input[key];
      if (value === "" || value === undefined) {
        ///@ts-ignore
        delete this._input[key];
      } else if (Array.isArray(value) && value.length === 0) {
        ///@ts-ignore
        delete this._input[key];
      } else {
        let newKey = key.replace("_", "-");
        this._inputQueryObj[newKey] = value;
      }
    }
  }

  static requestSearch(addMode: "replace" | "add" = "replace") {
    let queryString = generateSearchQuery(this._inputQueryObj);
    // 如果非有效搜尋條件的話
    if (!queryString)
      return;
    let queryUrl_withoutPageIndex = GS.search.getQueryUrl(queryString);
    if (this._inputQueryObj["page-index"] !== undefined)
      queryString += "&page-index=" + this._inputQueryObj["page-index"];

    if (this._queueQuery) {
      if (this._queueQuery.query === queryString) {
        console.log("same query as queue query");
        return;
      }
    } else if (this._lastQuery && this._lastQuery.query === queryString) {
      console.log("same query as last query");
      return;
    }

    let success = this._searchResult.request(this._input, addMode);
    this._searchResult.toggleAutoLoad(false);
    Utility.replaceUrlParams(queryString, false);
    if (success) {
      this._lastQuery = {
        queryUrl: queryUrl_withoutPageIndex,
        query: queryString,
        input: Object.assign({}, this._input)
      }
    } else {
      this._queueQuery = {
        queryUrl: queryUrl_withoutPageIndex,
        query: queryString,
        input: Object.assign({}, this._input)
      }
    }
  }

  static getSearchText() {
    let array = [];

    if (this._input.keyword)
      array.push(this._input.keyword);
    if (this._input.sort)
      array.push(this._input.sort);
    if (this._input.start_date && this._input.end_date) {
      ///@ts-ignore
      array.push(this.getDateText(moment(this._input.start_date), moment(this._input.end_date)));
    }

    if (this._input.start_time && this._input.end_time)
      array.push(this._input.start_time + "~" + this._input.end_time);

    if (this._input.min_price !== undefined &&
      !(this._input.min_price === _priceSetting.min && this._input.max_price === _priceSetting.max)
    )
      array.push(`NT$${this._input.min_price}-$NT$${this._input.max_price}`);

    if (this._input.site_list && this._input.site_list.length) {
      let string = "";
      this._input.site_list.forEach((siteId) => {
        if (string.length > 0)
          string += "/";
        string += siteId;
      });
      array.push(string);
    }

    return array.join(".");
  }

  static getDateText(startDate: moment.Moment, endDate: moment.Moment) {
    return startDate.format('MM/DD/YYYY') + ' - ' + endDate.format('MM/DD/YYYY');
  }
}

/**
 * 產生用於網址的搜尋參數(不包含 page-index)
 * @param input 搜尋條件
 * @returns 搜尋參數
 */
export function generateSearchQuery(input: any) {
  let array: string[] = [];
  input.needSend = false;

  addFieldToList(array, "keyword", input);
  addFieldToList(array, "site-list", input);
  addFieldToList(array, "sort", input);
  addFieldToList(array, "start-date", input);
  addFieldToList(array, "end-date", input);
  addFieldToList(array, "start-time", input);
  addFieldToList(array, "end-time", input);
  addFieldToList(array, "min-price", input);
  addFieldToList(array, "max-price", input);
  addFieldToList(array, "order", input);

  // 是否為有效搜尋條件(只有 order 的話為無效)
  if (input.needSend)
    return array.join("&");
  return undefined;
}

function addFieldToList(list: string[], fieldName: string, input: any, checkSend: boolean = true) {
  let value = input[fieldName];
  if (value !== undefined) {
    value = encodeURIComponent(value);
    list.push(`${fieldName}=${value}`);
    if (checkSend)
      input.needSend = true;
  }
}